import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/core/Meshes/Builders/planeBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";

import { TraceBehavior } from "./tracebehavior";
import { drivesValue } from "./bindingValues";
import { ImageAnchorData, ANCHOR_PREVIEW_MESH_NAME } from "../scenedata/behaviors/imageanchordata";
import { AnchorOrientation } from "../scenedata/common";
import { resolveAnchorPitchRadiansForRoot } from "../loaders/anchorOrientationHeuristic";
import type { SceneObject } from "../core/SceneObject";
import { Logger } from "../utils/Logger";

const PREVIEW_MESH_NAME = ANCHOR_PREVIEW_MESH_NAME;
const DEFAULT_WIDTH_METERS = 0.2;

/**
 * Describes an image-marker anchor for the scene.
 *
 * Attaches to the scene root and renders a preview plane of the printed
 * marker at its physical scale. Holds the marker's source URL, physical
 * width, and expected orientation (Horizontal/Vertical/Free).
 *
 * **CMS-managed.** This behavior's data lives on the CMS Scene record, not
 * in the `.trace` file — it is excluded from `.trace` serialization via the
 * `cmsManaged` static flag. Authored data still round-trips through the
 * publish-metadata channel (`getSceneObjectData().behaviors` on scene root).
 *
 * **Anchor coordinate convention.** The editor operates in the OpenXR /
 * WebXR tracked-image anchor frame (the `.zip` loader converts Unity-LH
 * → OpenXR-RH on import):
 *
 *   - `+X` = image-right.
 *   - `+Y` = image-top.
 *   - `+Z` = image-normal (out of the marker face, toward the viewer).
 *
 * Scene-root rotation maps this anchor frame to world per the
 * authored pitch (see `resolveAnchorPitchRadiansForRoot`):
 *   - Vertical: identity. Anchor +Z normal stays world +Z (faces camera).
 *   - Horizontal: `Rx(-π/2)`. Anchor +Z normal → world +Y (lies flat).
 *   - Free / unset with an authored pitch: `Rx(pitchDegrees)`.
 *   - Free / unset with no pitch: the content-detection heuristic picks
 *     vertical (0) or horizontal (`Rx(-π/2)`) from the authored children;
 *     with no children, vertical.
 *
 * **Preview mesh.** Babylon's `CreatePlane` builds geometry in the XY
 * plane — exactly the anchor's image plane (+X right, +Y top, normal Z).
 * The only tweaks are `sideOrientation: BACKSIDE` (RH backface culling
 * shows the `+Z` face — the marker face direction) and
 * `Texture(invertY=true)` (source-top row lands at vertex `+Y` =
 * anchor image-top, so the printed marker reads right-side-up). No UV
 * flipping, no plane rotation, no orientation-specific switch — preview
 * frame is fixed in anchor space and the scene-root rotation handles
 * the rest.
 */
export class ImageAnchor extends TraceBehavior<ImageAnchorData> {
    static readonly behaviorType = "ImageAnchor" as const;

    /**
     * Marks this behavior as CMS-managed — excluded from `.trace` export.
     * See SceneObject.serializeBehaviors().
     */
    static readonly cmsManaged = true;

    private _preview: Mesh | null = null;
    private _material: StandardMaterial | null = null;
    private _texture: Texture | null = null;
    /** Aspect ratio (width/height) of the loaded texture; 1 until loaded. */
    private _aspect: number = 1;
    /** Runtime-only preview visibility — not authored data. */
    private _previewEnabled = true;

    protected async applyCommit(patch: Partial<ImageAnchorData>): Promise<void> {
        // Reload the texture only when src changes
        if ('src' in patch) {
            await this._loadTexture();
        }
        this._rebuildPreview();
        // Orientation or pitch changes must update the scene-root rotation so
        // the editor visualization reflects the new pose immediately. Any
        // explicit commit of either field — including a reset to 0 / undefined
        // — is a deliberate authored pose and must run; the original "leave
        // the heuristic alone" guard only matters on initial setBehavior with
        // neither field present, which short-circuits here already.
        if ('orientation' in patch || 'pitchDegrees' in patch) {
            this._applySceneRootRotation();
        }
    }

    load(): Promise<void> {
        return this._loadTexture();
    }

    protected onAttach(_target: TransformNode): void {
        this._rebuildPreview();
        // Deliberately NOT applying scene-root rotation here. Initial-load
        // rotation is the consumer's responsibility (loaders call
        // `applyAnchorRotation`/`alignAnchorToWorld`). This keeps the viewer-app
        // XR path safe — adding an ImageAnchor doesn't compose with the XR
        // reference-space offset. Mid-session orientation toggles in the editor
        // go through `applyCommit`, which does apply rotation.
    }

    protected onDetach(): void {
        this._disposePreview();
        this._texture?.dispose();
        this._texture = null;
    }

    /**
     * Apply scene-root rotation based on this anchor's explicit orientation
     * and pitch. Orientation is the semantic label and wins when set to a
     * concrete pose; pitch fills in the angle when orientation is Free /
     * unset:
     *
     * - `Vertical` → identity (wall-mounted, +Z normal toward camera).
     *   pitchDegrees is ignored — orientation gives the complete pose.
     * - `Horizontal` → Rx(-π/2) (flat on table, +Z normal toward world +Y).
     *   pitchDegrees is ignored.
     * - `Free` or unset → use `pitchDegrees` as the absolute pitch about
     *   the local X axis (image-right). Default 0° = standing up like
     *   Vertical.
     *
     * Children's world poses are preserved across the toggle: each direct
     * SceneObject child is counter-rotated so its world position and rotation
     * stay fixed. This matches the editor mental model — toggling "is this a
     * wall or table marker?" doesn't move your authored content, it just
     * relabels how the marker is interpreted.
     *
     * The marker preview mesh (a raw child of scene-root transform, not a
     * SceneObject) is intentionally NOT preserved — it should rotate with the
     * anchor so it always lies flat in the marker's local frame.
     *
     * Camera is NOT updated here — the user keeps their current viewing angle
     * when toggling orientation. Re-call `alignAnchorToWorld` if the camera
     * needs to be reframed.
     */
    private _applySceneRootRotation(): void {
        const explicit = this._resolved.orientation;
        const authoredPitch = this._resolved.pitchDegrees;

        const sceneRoot = this.sceneObject;
        const oldRootRot = sceneRoot.transform.rotationQuaternion?.clone() ?? Quaternion.Identity();
        const oldRootPos = sceneRoot.transform.position.clone();

        // Capture children's world poses before we mutate the root.
        // Keyed by SceneObject identity (names aren't unique).
        interface Snapshot { worldRot: Quaternion; worldPos: Vector3 }
        const snapshots = new Map<SceneObject, Snapshot>();
        const children = sceneRoot.getChildren();
        for (const child of children) {
            child.transform.computeWorldMatrix(true);
            const localRot = child.transform.rotationQuaternion?.clone()
                ?? Quaternion.FromEulerVector(child.transform.rotation);
            snapshots.set(child, {
                // worldRot = parentRot * localRot. Babylon's
                // `absoluteRotationQuaternion` is a getter, but we compute
                // explicitly here so the formula matches the restore below.
                worldRot: oldRootRot.multiply(localRot),
                worldPos: child.transform.getAbsolutePosition().clone(),
            });
        }

        // Resolve the pitch through the shared resolver so a `Free`/unset
        // marker with no authored pitch consults the content-detection
        // heuristic — exactly as the loader's `applyAnchorRotation` does.
        // Without this, re-committing a `Free` marker (e.g. TraceFrontend's
        // post-load StudioImageAnchorSync) fell through to the default
        // 0 = vertical and flipped a horizontal table marker upright, 90° off
        // its content. Children still hold their pre-recommit local rotations
        // here (the counter-rotation below runs after), so the heuristic sees
        // the same content the loader saw.
        const pitch = resolveAnchorPitchRadiansForRoot(
            explicit,
            authoredPitch,
            children,
        );
        const newRootRot = Quaternion.RotationYawPitchRoll(0, pitch, 0);
        sceneRoot.transform.rotationQuaternion = newRootRot;

        // Restore each child's world pose under the new root rotation:
        //   newLocalRot = newRootRot⁻¹ * oldWorldRot
        //   newLocalPos = newRootRot⁻¹ * (oldWorldPos - rootPos)
        const newRootRotInv = newRootRot.clone();
        newRootRotInv.invertInPlace();
        for (const child of children) {
            const snap = snapshots.get(child);
            if (!snap) continue;
            child.transform.rotationQuaternion = newRootRotInv.multiply(snap.worldRot);
            const offset = snap.worldPos.subtract(oldRootPos);
            const newLocal = new Vector3(0, 0, 0);
            offset.rotateByQuaternionToRef(newRootRotInv, newLocal);
            child.transform.position.copyFrom(newLocal);
        }

        Logger.debug(`[ImageAnchor] Scene-root rotation applied for orientation=${explicit ?? 'unset'} pitch=${(pitch * 180 / Math.PI).toFixed(2)}°, ${children.length} child world pose(s) preserved`);
    }

    /** Load the marker texture; updates `_aspect`. Resolves on success or failure. */
    private _loadTexture(): Promise<void> {
        // Dispose previous texture
        this._texture?.dispose();
        this._texture = null;
        this._aspect = 1;

        // A bound marker src that no provider declares is not a URL — loading
        // it 404s and leaves the anchor with a broken texture instead of none.
        const src = this._resolved.src;
        if (!drivesValue(src)) return Promise.resolve();

        return new Promise<void>(resolve => {
            const tex = new Texture(
                src,
                this.scene,
                false,
                true,
                Texture.BILINEAR_SAMPLINGMODE,
                () => {
                    const { width, height } = tex.getSize();
                    this._aspect = height > 0 ? width / height : 1;
                    this._texture = tex;
                    resolve();
                },
                (msg) => {
                    Logger.warn(`[ImageAnchor] Texture error for "${src}": ${msg}`);
                    tex.dispose();
                    resolve();
                },
            );
        });
    }

    /** Build (or rebuild) the preview mesh on the scene-root transform. */
    private _rebuildPreview(): void {
        this._disposePreview();

        // No texture loaded — nothing to render
        if (!this._texture) return;

        // OpenXR anchor frame: +X = image-right, +Y = image-top,
        // +Z = image-normal. Babylon `CreatePlane` already builds a plane
        // in the XY plane — exactly the anchor's image plane. The only
        // tweak: Babylon RH renders FRONTSIDE on the -Z face, but we
        // want the +Z face visible (the marker face direction). Use
        // BACKSIDE so the +Z face is the visible one.
        const plane = MeshBuilder.CreatePlane(
            PREVIEW_MESH_NAME,
            { size: 1, sideOrientation: Mesh.BACKSIDE },
            this.scene,
        );

        const widthMeters = this._resolved.widthMeters && this._resolved.widthMeters > 0
            ? this._resolved.widthMeters
            : DEFAULT_WIDTH_METERS;

        plane.scaling = new Vector3(this._aspect * widthMeters, widthMeters, 1);

        const material = new StandardMaterial(`${PREVIEW_MESH_NAME}_mat`, this.scene);
        material.emissiveTexture = this._texture;
        material.disableLighting = true;
        plane.material = material;

        plane.parent = this.sceneObject.transform;
        plane.setEnabled(this._previewEnabled);

        this._preview = plane;
        this._material = material;
    }

    private _disposePreview(): void {
        this._preview?.dispose();
        this._preview = null;
        this._material?.dispose();
        this._material = null;
    }

    /**
     * Authored marker orientation. `undefined` means the user hasn't chosen
     * one yet — consumers should treat that as "use the legacy detection
     * heuristic." `Free` is an explicit choice meaning "no runtime snap";
     * for editor visualization it behaves like Horizontal.
     */
    get orientation(): AnchorOrientation | undefined {
        return this._resolved.orientation;
    }

    /**
     * Authored marker pitch in degrees, about the marker's local X axis.
     * Used by the preview rotation only when `orientation` is `Free` or
     * unset — concrete orientations (`Vertical`/`Horizontal`) already
     * give the complete pose and override this value.
     */
    get pitchDegrees(): number {
        return this._resolved.pitchDegrees ?? 0;
    }

    /**
     * Show or hide the marker preview mesh. Runtime-only (never authored or
     * serialized) and survives preview rebuilds. An editor that draws its
     * own marker — the localizer placement editor draws one quad per
     * placement — hides this one so the two don't z-fight.
     */
    setPreviewEnabled(enabled: boolean): void {
        this._previewEnabled = enabled;
        this._preview?.setEnabled(enabled);
    }

    /** Whether the marker preview mesh is shown (see {@link setPreviewEnabled}). */
    get previewEnabled(): boolean {
        return this._previewEnabled;
    }

    /** Get the effective marker physical width in meters. */
    get widthMeters(): number {
        return this._resolved.widthMeters && this._resolved.widthMeters > 0
            ? this._resolved.widthMeters
            : DEFAULT_WIDTH_METERS;
    }
}
